/**
 * ADR-0185 Phase B iteration 1 — Find (owner's pick 2a, 2026-09-10).
 *
 * Only the Refined track is movable, and a planner cannot act on a job he
 * cannot locate among 18 lanes. This is the locator: a box in the board
 * header — never a modal, never a filter. It jumps to the job and highlights
 * it on every track; the lane stack keeps all its machines, because the
 * context around a job is exactly what a move decision needs.
 *
 * `/` focuses it from anywhere on the board (unless the caret is already in a
 * field), Enter and Shift+Enter walk the matches, Esc clears.
 */
import { useEffect, useRef } from "react";
import type { FindMatch } from "./laneMath.js";

export function FindBox(props: {
  query: string;
  onQueryChange: (q: string) => void;
  matches: FindMatch[];
  /** 1-based position of the active match, or 0 when nothing is active. */
  position: number;
  onStep: (delta: number) => void;
  onClear: () => void;
}) {
  const ref = useRef<HTMLInputElement | null>(null);

  // "/" focuses the box from anywhere on the board. Typing "/" inside any
  // field must still type a slash, so the shortcut stands down whenever the
  // caret is in an input, a textarea or a contenteditable.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "/" || e.metaKey || e.ctrlKey || e.altKey) return;
      const el = document.activeElement as HTMLElement | null;
      const tag = el?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || el?.isContentEditable) return;
      e.preventDefault();
      ref.current?.focus();
      ref.current?.select();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const searching = props.query.trim() !== "";
  const none = searching && props.matches.length === 0;

  return (
    <div className="boardFind">
      <input
        ref={ref}
        type="search"
        className="input boardFindInput"
        placeholder="Find job or part"
        aria-label="Find a job or part"
        value={props.query}
        onChange={(e) => props.onQueryChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            props.onStep(e.shiftKey ? -1 : 1);
          } else if (e.key === "Escape") {
            e.preventDefault();
            props.onClear();
          }
        }}
      />
      {/* The count is the whole stepper's affordance: "3 of 7" plus the two
          arrows. It appears only while a search is live, so the header stays
          quiet when nobody is looking for anything. */}
      {searching && props.matches.length > 0 ? (
        <span className="boardFindCount" aria-live="polite">
          <span className="muted">
            {props.position} of {props.matches.length}
          </span>
          {props.matches.length > 1 ? (
            <>
              <button type="button" className="btn btnSm" aria-label="Previous match" onClick={() => props.onStep(-1)}>
                ↑
              </button>
              <button type="button" className="btn btnSm" aria-label="Next match" onClick={() => props.onStep(1)}>
                ↓
              </button>
            </>
          ) : null}
        </span>
      ) : null}
      {none ? (
        <span className="muted boardFindEmpty" aria-live="polite">
          No job or part matches
        </span>
      ) : null}
    </div>
  );
}
